import React from "react";
import { TestResult } from "../types";
interface ResultCardProps {
  result: TestResult | null;
}

const ResultCard: React.FC<ResultCardProps> = ({ result }) => {
  if (!result?.primaryArchetype) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Error Loading Results
        </h2>
        <p className="text-gray-600">
          We encountered an issue loading your results. Please try refreshing
          the page or contact support.
        </p>
      </div>
    );
  }

  const { primaryArchetype } = result;

  const getArchetypeIcon = (archetype: string, size: number = 24) => {
    switch (archetype) {
      case "jaguar":
        return (
          <img
            src="/archetype_jaguar.jpg"
            alt="Jaguar"
            className="border rounded-full special-icon-feature"
          />
        );
      case "condor":
        return (
          <img
            src="/archetype_condor.jpg"
            alt="Condor"
            className="border rounded-full special-icon-feature"
          />
        );
      case "llama":
        return (
          <img
            src="/archetype_llama.jpg"
            alt="Llama"
            className="border rounded-full special-icon-feature"
          />
        );
      case "quetzal":
        return (
          <img
            src="/archetype_quetzal.jpg"
            alt="Quetzal"
            className="border rounded-full special-icon-feature"
          />
        );
      case "colibri":
        return (
          <img
            src="/archetype_colibri.jpg"
            alt="Colibri"
            className="border rounded-full special-icon-feature"
          />
        );
      case "coqui":
        return (
          <img
            src="/archetype_coqui.jpg"
            alt="Coqui"
            className="border rounded-full special-icon-feature"
          />
        );
      case "buho":
        return (
          <img
            src="/archetype_buho.jpg"
            alt="Buho"
            className="border rounded-full special-icon-feature"
          />
        );
      default:
        return (
          <img
            src="/all_7_archetypes_together.png"
            alt="Default Archetype"
            className="border rounded-full special-icon-feature"
          />
        );
    }
  };

  const getSubTitle = (archetype: string): string => {
    switch (archetype) {
      case "jaguar":
        return "El Triunfador Decidido";
      case "condor":
        return "El Estratega Visionario";
      case "llama":
        return "La Colaboradora Leal";
      case "quetzal":
        return "El Creativo Auténtico";
      case "colibri":
        return "La Chispa Alegre";
      case "coqui":
        return "El Defensor de la Comunidad";
      case "buho":
        return "El Planificador Analítico";
      default:
        return "El explorador curioso";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div
        className="p-6"
        style={{ backgroundColor: primaryArchetype.color + "20" }}
      >
        <div className="flex items-center mb-4 flex-mob-column">
          <div
            className="rounded-full mr-4"
            style={{ backgroundColor: primaryArchetype.color }}
          >
            {getArchetypeIcon(primaryArchetype.id, 28)}
          </div>
          <div className="flex flex-col gap-3">
            <h2 className="text-4xl font-bold text-gray-800">
              Eres una {primaryArchetype.name} -{" "}
              {getSubTitle(primaryArchetype.id)}
            </h2>
            <p className="text-gray-600 text-2xl font-bold italic">
              {primaryArchetype.spanishName}
            </p>
            <hr className="my-2 border-gray-300" />
            <p
              className="text-xl text-gray-700 mb-6"
              dangerouslySetInnerHTML={{ __html: primaryArchetype.description }}
            ></p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 border-b-2 border-gray-800 pb-2">
            Tus dones naturales incluyen:
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            {primaryArchetype.strengths.map((strength, index) => (
              <li key={index} className="text-gray-700">
                {strength}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 border-b-2 border-gray-800 pb-2">
            Por qué importa
          </h3>
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{ __html: primaryArchetype.workStyle }}
          />
        </div>

        <div className="mb-6">
          <h3
            className="text-lg font-semibold mb-2 border-b-2 border-gray-800 pb-2 cursor-pointer"
            onClick={() => {
              const btn = document.getElementById("premium-cta-button");
              if (btn) btn.click();
            }}
          >
            Desbloquea tu máximo potencial por{" "}
            <span style={{ textDecoration: "line-through" }}>$15</span> $7
          </h3>
          <p className="text-gray-700">
            Tu <b>Reporte Premium Personalizado</b> incluye:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>7 páginas repletas de estrategias profesionales, consejos expertos e ideas prácticas</li>
            <li>Trayectoria profesional ideal</li>
            <li>Narrativa completa de tu arquetipo</li>
            <li>Fortalezas del arquetipo</li>
            <li>Áreas de crecimiento</li>
            <li>Consejos profesionales</li>
            <li>Ruta profesional personalizada</li>
            <li>Roles profesionales ideales</li>
            <li>Roles a evitar</li>
            <li>Estilo de trabajo preferido</li>
            <li>Entorno ideal</li>
            <li>Prompts de IA para tu crecimiento</li>
          </ul>
          <p className="text-gray-700">...y mucho más</p>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 border-b-2 border-gray-800 pb-2">
            {primaryArchetype.careerTitle}
          </h3>
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{ __html: primaryArchetype.careerAdvice }}
          ></p>
        </div>

        <div className="mb-6">
          <h3
            className="text-lg font-semibold mb-2 border-b-2 border-gray-800 pb-2 cursor-pointer"
            onClick={() => {
              const btn = document.getElementById("premium-cta-button");
              if (btn) btn.click();
            }}
          >
            {" "}
            👉 [Desbloquea tu Reporte Completo por{" "}
            <span style={{ textDecoration: "line-through" }}>$15</span> $7]
          </h3>
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{
              __html: primaryArchetype.overallComment,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ResultCard;
